import { FC, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, matchPath, Route, Routes, useLocation } from 'react-router-dom';
import { navigateToMicroApp, syncOrchestratorUrl } from '@dvag/dfs-orchestrator-client/navigation';
import { Error404 } from '@dvag/dfs-ui-blocks/components';

import { route } from 'config/route';
import { ManageHousehold } from 'screen/ManageHousehold';
import { SavingType } from 'screen/SavingType/SavingType';
import { PersonalDataForm } from './screen/PersonalDataForm';
import { RouterErrorBoundary } from './RouterErrorBoundary';
import { WuenscheUndZieleRoute } from './component/WuenscheUndZieleWebComponent/WuenscheUndZieleRoute';
import { sharedAppRoutes } from '@dvag/dfs-constant-config/shared-app-names';
import { appNames } from '@dvag/dfs-constant-config/app-names';
import { KundenstammdatenNamensaenderung } from './component/KundenstammdatenNamensaenderung/KundenstammdatenNamensaenderung';
import { AddressEdit } from './screen/AddressEdit/AddressEdit';
import { KundenstammdatenBankverbindungsaenderung } from './component/KundenstammdatenBankverbindungsaenderung/KundenstammdatenBankverbindungsaenderung';
import { keydownF8EventListener } from '@dvag/dfs-orchestrator-client';

const {
  person,
  manageHousehold,
  manageHouseholdWithReturnUrl,
  createHousehold,
  wishesAndGoals,
  wishesAndGoalsWithSelectedPerson,
  wishesAndGoalsSalesforce,
  wishesAndGoalsSalesforceWithSelectedPerson,
  wishesAndGoalsHideFinancialGrade,
  wishesAndGoalsHideFinancialGradeWithSelectedPerson,
  wishesAndGoalsServiceAnalyse,
  wishesAndGoalsServiceAnalyseWithSelectedPerson,
  savingType,
  personWithActiveForm,
  addressEdit,
  kssNameChange,
  kssBankDetailsChange,
} = route;

const navigateToAdvisorDashboard = () => {
  navigateToMicroApp(appNames.advisor, sharedAppRoutes.advisor.householdSearch);
};

export const App = () => {
  const isInitialMount = useRef(true);
  const location = useLocation();

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (location.key && !matchPath(manageHouseholdWithReturnUrl, location.pathname)) {
      syncOrchestratorUrl(`${location.pathname}`);
    }
  }, [location]);

  const householdId = Object.values(route)
    .map((path) => matchPath(path, location.pathname)?.params.householdId)
    .find(Boolean);

  useEffect(() => {
    if (!householdId) {
      return;
    }

    return keydownF8EventListener(window, householdId);
  }, [householdId]);

  return (
    <Suspense fallback={null}>
      <RouterErrorBoundary>
        <Routes>
          <Route path={person} element={<PersonalDataForm />} />
          <Route path={addressEdit} element={<AddressEdit />} />
          <Route path={personWithActiveForm} element={<PersonalDataForm />} />
          <Route path={savingType} element={<SavingType />} />
          <Route path={createHousehold} element={<ManageHousehold />} />
          <Route path={manageHousehold} element={<ManageHousehold />} />
          <Route path={manageHouseholdWithReturnUrl} element={<ManageHousehold />} />
          <Route path={wishesAndGoals} element={<WuenscheUndZieleRoute />} />
          <Route path={wishesAndGoalsWithSelectedPerson} element={<WuenscheUndZieleRoute />} />
          <Route path={wishesAndGoalsSalesforce} element={<WuenscheUndZieleRoute isSalesforce />} />
          <Route
            path={wishesAndGoalsSalesforceWithSelectedPerson}
            element={<WuenscheUndZieleRoute isSalesforce />}
          />
          <Route
            path={wishesAndGoalsHideFinancialGrade}
            element={<WuenscheUndZieleRoute hideFinancialGrade />}
          />
          <Route
            path={wishesAndGoalsHideFinancialGradeWithSelectedPerson}
            element={<WuenscheUndZieleRoute hideFinancialGrade />}
          />
          <Route
            path={wishesAndGoalsServiceAnalyse}
            element={<WuenscheUndZieleRoute hideNavigation />}
          />
          <Route
            path={wishesAndGoalsServiceAnalyseWithSelectedPerson}
            element={<WuenscheUndZieleRoute hideNavigation />}
          />
          <Route path={kssNameChange} element={<KundenstammdatenNamensaenderung />} />
          <Route
            path={kssBankDetailsChange}
            element={<KundenstammdatenBankverbindungsaenderung />}
          />
          <Route path="*" element={<Error404 onAction={navigateToAdvisorDashboard} />} />
        </Routes>
      </RouterErrorBoundary>
    </Suspense>
  );
};

export const Router: FC = () => (
  <BrowserRouter>
    <App />
  </BrowserRouter>
);
